import Seo from '@/components/Seo';
import { SITE } from '@/lib/site';
import { getSeo } from '@/lib/seo';

export default function Privacy() {
  return (
    <>
      <Seo
        path="/privacy"
        fullTitle={getSeo('privacy').title}
        description={getSeo('privacy').description}
        ogImage={getSeo('privacy').og_image ?? undefined}
      />
      <article className="mx-auto max-w-prose px-5 py-20 sm:px-8 sm:py-24">
        <p className="text-sm font-medium uppercase tracking-wider text-accent">
          Privacy
        </p>
        <h1 className="mt-3 font-display text-4xl">Privacy notice</h1>
        <p className="mt-2 text-sm text-fg-subtle">
          Last updated: 26 April 2026
        </p>

        <div className="prose prose-invert mt-10 space-y-6 text-fg-muted">
          <p>
            This notice explains what personal information {SITE.name} collects,
            how we use it, and what your rights are. We've kept the language
            plain — no legalese where we can avoid it.
          </p>

          <H2>Who we are</H2>
          <p>
            {SITE.name} is an online-only web design and hosting studio based
            in the United Kingdom. For the purposes of UK data protection law
            (UK GDPR and the Data Protection Act 2018), we are the "data
            controller" for any personal data you submit to us via this site.
          </p>
          <p>
            You can reach us at{' '}
            <a className="link" href={`mailto:${SITE.email}`}>
              {SITE.email}
            </a>{' '}
            or {SITE.phone}.
          </p>

          <H2>What we collect</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Contact form submissions</strong>: name, email address,
              optional phone number, your message, and your stated budget.
            </li>
            <li>
              <strong>Admin sessions</strong>: a single cookie used to keep
              authorised staff signed into the admin area.
            </li>
            <li>
              <strong>Server logs</strong>: standard request metadata
              (timestamp, hashed IP, user-agent) kept for up to 30 days for
              security and abuse-prevention.
            </li>
            <li>
              <strong>Google Ads measurement</strong> (only with your consent):
              if you click <em>Accept</em> on our cookie banner, we load
              Google's gtag script. It sets cookies that let us measure which
              of our adverts brought you to the site and whether you
              submitted an enquiry afterwards. If you click <em>Decline</em>{' '}
              (or never make a choice) the script never loads and no Google
              cookies are set.
            </li>
          </ul>

          <H2>Why we hold it, and how long</H2>
          <p>
            We use contact-form data only to reply to your enquiry and, if you
            become a client, to deliver the service you've asked for. Enquiries
            we don't take forward are deleted within 12 months. Active client
            records are kept for as long as the relationship lasts plus six
            years for tax purposes, then deleted.
          </p>
          <p>
            Our lawful bases are: <em>consent</em> (when you submit the form),
            <em> contract</em> (to deliver work you've engaged us for), and
            <em> legitimate interests</em> (to keep our systems secure).
          </p>

          <H2>Who we share it with</H2>
          <p>
            We use a small number of carefully chosen processors to operate the
            site: a hosting provider (Cloudflare) and an email-sending service
            (Resend). We don't sell or trade personal data, and we don't use it
            for marketing without your explicit consent.
          </p>

          <H2>Your rights</H2>
          <p>
            You have the right to access, correct, port, restrict, or erase any
            personal data we hold about you, and to withdraw consent at any
            time. To exercise any of these, email us at{' '}
            <a className="link" href={`mailto:${SITE.email}`}>
              {SITE.email}
            </a>
            . You also have the right to complain to the UK Information
            Commissioner's Office (ICO) at <a className="link" href="https://ico.org.uk">ico.org.uk</a>.
          </p>

          <H2>Cookies</H2>
          <p>
            We use a single strictly-necessary cookie (<code>yd_session</code>)
            to keep authorised staff signed into the admin area. We use
            browser <code>localStorage</code> to remember your theme choice
            (dark/light) and your cookie-banner choice. If you accept the
            optional Google Ads tracking, Google sets additional cookies
            (typically <code>_gcl_au</code> and similar) on the
            <code>.googleadservices.com</code> and <code>.google.com</code>{' '}
            domains.
          </p>
          <p>
            To withdraw consent later, clear this site's data in your browser —
            you'll be re-prompted on your next visit and can choose Decline.
          </p>

          <H2>Changes</H2>
          <p>
            If we change anything material in this notice we'll update the
            "last updated" date and, where appropriate, contact you directly.
          </p>
        </div>
      </article>
    </>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-10 text-2xl text-fg-base">{children}</h2>;
}
